import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DatePicker, Radio, Segmented, Select } from 'antd';
import { Dayjs } from 'dayjs';
import Button from '../../../../../components/Button';
import { PromotionScheduleStatus, PromotionScheduleTrack } from '../../../../../lib/api/promotionSchedule';
import {
  MANAGER_UNASSIGNED_VALUE,
  PERIOD_UNIT_LABEL,
  PERIOD_UNIT_PICKER,
  PeriodUnit,
  STATUS_LABEL,
  TRACK_META,
  TRACK_ORDER,
  VIEW_MODE,
  ViewMode,
} from '../../constants';
import { promotionScheduleQuery } from '../../queries';
import { StyledFilterCard, StyledFilterRow, StyledLabel, StyledRightGroup } from './styles';

const { RangePicker } = DatePicker;

type Props = {
  periodUnit: PeriodUnit;
  range: [Dayjs, Dayjs];
  tracks: PromotionScheduleTrack[];
  statuses: PromotionScheduleStatus[];
  adminIds: number[];
  viewMode: ViewMode;
  canCreate: boolean;
  onChangePeriodUnit: (periodUnit: PeriodUnit) => void;
  onChangeRange: (range: [Dayjs, Dayjs]) => void;
  onChangeTracks: (tracks: PromotionScheduleTrack[]) => void;
  onChangeStatuses: (statuses: PromotionScheduleStatus[]) => void;
  onChangeAdminIds: (adminIds: number[]) => void;
  onChangeViewMode: (viewMode: ViewMode) => void;
  onClickCreate: () => void;
};

const TRACK_OPTIONS = TRACK_ORDER.map((track) => ({
  value: track,
  label: TRACK_META[track].label,
}));

const STATUS_OPTIONS = (Object.keys(STATUS_LABEL) as PromotionScheduleStatus[]).map((status) => ({
  value: status,
  label: STATUS_LABEL[status],
}));

const PERIOD_UNIT_OPTIONS = (Object.keys(PERIOD_UNIT_LABEL) as PeriodUnit[]).map((unit) => ({
  value: unit,
  label: PERIOD_UNIT_LABEL[unit],
}));

/**
 * 타임라인 필터.
 *
 * 뷰 전환(간트/캘린더)을 여기 두는 이유 — 두 뷰는 같은 데이터의 다른 투영이라
 * 필터 상태를 공유해야 한다. 메뉴로 나누면 전환할 때마다 기간·트랙 선택이 초기화된다.
 */
export const TimelineFilter = (props: Props): React.JSX.Element => {
  const {
    periodUnit,
    range,
    tracks,
    statuses,
    adminIds,
    viewMode,
    canCreate,
    onChangePeriodUnit,
    onChangeRange,
    onChangeTracks,
    onChangeStatuses,
    onChangeAdminIds,
    onChangeViewMode,
    onClickCreate,
  } = props;

  // ScheduleFormModal 과 같은 쿼리 키라 캐시를 공유한다
  const { data: managers = [] } = useQuery({
    ...promotionScheduleQuery.managers,
    staleTime: 60_000,
  });

  const managerOptions = useMemo(
    () => [
      { value: MANAGER_UNASSIGNED_VALUE, label: '(담당자 미지정)' },
      ...managers.map((manager) => ({
        value: manager.id,
        label: manager.department ? `${manager.name} (${manager.department})` : manager.name,
      })),
    ],
    [managers],
  );

  return (
    <StyledFilterCard>
      <StyledFilterRow>
        <StyledLabel>조회 기간</StyledLabel>
        <Segmented
          value={periodUnit}
          options={PERIOD_UNIT_OPTIONS}
          onChange={(value) => onChangePeriodUnit(value as PeriodUnit)}
        />
        <RangePicker
          picker={PERIOD_UNIT_PICKER[periodUnit]}
          value={range}
          allowClear={false}
          onChange={(value) => {
            if (value?.[0] && value?.[1]) {
              onChangeRange([value[0], value[1]]);
            }
          }}
        />

        <StyledLabel>트랙</StyledLabel>
        <Select
          mode="multiple"
          allowClear={true}
          style={{ minWidth: 220 }}
          placeholder="전체"
          maxTagCount="responsive"
          value={tracks}
          options={TRACK_OPTIONS}
          onChange={onChangeTracks}
        />

        <StyledLabel>상태</StyledLabel>
        <Select
          mode="multiple"
          allowClear={true}
          style={{ minWidth: 150 }}
          placeholder="전체"
          maxTagCount="responsive"
          value={statuses}
          options={STATUS_OPTIONS}
          onChange={onChangeStatuses}
        />

        <StyledLabel>담당자</StyledLabel>
        <Select
          mode="multiple"
          allowClear={true}
          showSearch={true}
          optionFilterProp="label"
          style={{ minWidth: 200 }}
          placeholder="전체"
          maxTagCount="responsive"
          value={adminIds}
          options={managerOptions}
          onChange={onChangeAdminIds}
        />

        <StyledRightGroup>
          <Radio.Group
            value={viewMode}
            optionType="button"
            buttonStyle="solid"
            onChange={(event) => onChangeViewMode(event.target.value as ViewMode)}
          >
            <Radio.Button value={VIEW_MODE.GANTT}>간트</Radio.Button>
            <Radio.Button value={VIEW_MODE.CALENDAR}>캘린더</Radio.Button>
          </Radio.Group>

          {canCreate && <Button text="스케줄 등록" color="primary" onClick={onClickCreate} />}
        </StyledRightGroup>
      </StyledFilterRow>
    </StyledFilterCard>
  );
};
